'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var tslib_es6 = require('../tslib.es6-cd939937.js');
var logger_helpers = require('./helpers.js');
var configService = require('../configService.js');
var deepMerge = require('../deepMerge.js');

var CONSOLE_METHODS = [
    'debug',
    'error',
    'info',
    'log',
    'warn',
    'dir',
    'dirxml',
    'table',
    'trace',
    'group',
    'groupCollapsed',
    'groupEnd',
    'clear',
    'count',
    'countReset',
    'assert',
    'time',
    'timeLog',
    'timeEnd',
    'timeStamp',
];
var BaseLoader = /** @class */ (function () {
    function BaseLoader() {
    }
    /**
     * @method force
     * @description allow calling any logger method in chain mode even if log level not allow it
     * @alias f
     * */
    BaseLoader.prototype.force = function () {
        this.forcePrint = true;
        return this.proxyHandler;
    };
    /**
     * @method f
     * @description alias for force method
     * */
    BaseLoader.prototype.f = function () {
        return this.force();
    };
    /**
     * @method childLogger
     * @description create child logger with saving output path
     * @alias cl
     * */
    BaseLoader.prototype.childLogger = function (groupName, debugMode) {
        if (debugMode === void 0) { debugMode = false; }
        if (logger_helpers.isIE()) {
            this.warn('subGroup Not supported in IE!');
            return this.proxyHandler;
        }
        var baseLogger;
        if (this instanceof ChildLogger) {
            baseLogger = this.baseLogger;
        }
        else if (this instanceof StyledLogger) {
            baseLogger = this;
        }
        return new ChildLogger(groupName, this.originalPrefix, baseLogger, debugMode);
    };
    /**
     * @method cl
     * @description alias for subGroup method
     * */
    BaseLoader.prototype.cl = function (groupName, debugMode) {
        if (debugMode === void 0) { debugMode = true; }
        return this.childLogger(groupName, debugMode);
    };
    /**
     * @method path
     * @description create one more level for output only for current call
     * @alias p
     * */
    BaseLoader.prototype.path = function (path) {
        var separator = '/';
        if (this instanceof StyledLogger) {
            separator = this.config.get('style.separator');
        }
        else if (this instanceof ChildLogger) {
            separator = this.baseLogger.config.get('style.separator');
        }
        this.outputPrefix = tslib_es6.__spreadArray(tslib_es6.__spreadArray([], this.originalPrefix), path.split(separator));
        return this.proxyHandler;
    };
    /**
     * @method p
     * @description alias for path method
     * */
    BaseLoader.prototype.p = function (label) {
        return this.path(label);
    };
    return BaseLoader;
}());
var ProxyPolyfill = function (target, baseLogger) {
    if (!baseLogger && target instanceof StyledLogger) {
        baseLogger = target;
    }
    baseLogger.config.get('wrapConsoleMethods').forEach(function (method) {
        var usedMethod = console[method] !== undefined ? method : 'log';
        target[method] = function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            if (inLogLevel(baseLogger, method) || target.debugMode || baseLogger.forcePrint) {
                ConsoleClass[usedMethod].apply(ConsoleClass, args);
            }
        };
    });
    return target;
};
function proxyWrapper(ctx, baseLogger) {
    return {
        get: function (target, key) {
            var _a;
            if (!baseLogger) {
                baseLogger = target;
            }
            if (baseLogger.config.get('wrapConsoleMethods').includes(key)) { // if it calls any of console methods like (log, warn, info)
                if (inLogLevel(baseLogger, key) || target.debugMode || target.forcePrint) { // if it's globally defied debug level or for this group defined force debug mode
                    if (baseLogger.config.get('formattingOutputFor').includes(key)) { // methods for which need to formatting output
                        var params = logger_helpers.formatConsoleOutput(logger_helpers.formatByDelimiters(target.outputPrefix, baseLogger.config.get('style.delimiter')), baseLogger.config.get('style.maxDeepStyling'));
                        target.outputPrefix = target.originalPrefix;
                        target.forcePrint = false;
                        var usedMethod = ConsoleClass[key] !== undefined ? key : 'log';
                        return (_a = ConsoleClass[usedMethod]).bind.apply(_a, tslib_es6.__spreadArray([ConsoleClass], params));
                    }
                    else {
                        target.outputPrefix = target.originalPrefix;
                        target.forcePrint = false;
                        var usedMethod = ConsoleClass[key] !== undefined ? key : 'log';
                        return ConsoleClass[usedMethod].bind(ConsoleClass);
                    }
                }
                else {
                    target.outputPrefix = target.originalPrefix;
                    target.forcePrint = false;
                    return function () {
                        return null;
                    };
                }
            }
            else if (Reflect.has(target, key)) {
                // target.outputPrefix = [...path, name]
                return Reflect.get(target, key); // reed properties of instance
            }
            else if (Reflect.has(baseLogger, key)) {
                // target.outputPrefix = [...path, name]
                return Reflect.get(baseLogger, key); // reed properties of instance
            }
            return null;
        },
    };
}
var ConsoleClass = logger_helpers.isIE() ? logger_helpers.IEConsole : console;
function inLogLevel(baseLogger, key) {
    if (key === void 0) { key = null; }
    if (!baseLogger.config.get('debug.active')) {
        return false;
    }
    var level = baseLogger.config.get('debug.level');
    if (level === StyledLogger.DEBUG_LEVEL) { // allow any methods
        return true;
    }
    else if (level === StyledLogger.TRACE_LEVEL) {
        return ['error', 'assert', 'warn', 'trace'].includes(key);
    }
    else if (level === StyledLogger.WARN_LEVEL) {
        return ['error', 'assert', 'warn'].includes(key);
    }
    else if (level === StyledLogger.ERROR_LEVEL) {
        return ['error', 'assert'].includes(key);
    }
}
function isNativeFn(value) {
    return typeof value === 'function' && /\[native code\]/.test(value.toString());
}
var StyledLogger = /** @class */ (function (_super) {
    tslib_es6.__extends(StyledLogger, _super);
    function StyledLogger(name, confData) {
        if (confData === void 0) { confData = null; }
        var _this = _super.call(this) || this;
        _this.name = name;
        _this.config = new configService.ConfigService();
        var dat = deepMerge.deepMerge({
            debug: {
                active: true,
                level: StyledLogger.DEBUG_LEVEL,
            },
            style: {
                separator: '/',
                delimiter: ['[', ']'],
                maxDeepStyling: 6,
            },
            wrapConsoleMethods: CONSOLE_METHODS,
            formattingOutputFor: [
                'debug',
                'log',
                'info',
                'warn',
                'error',
                'group',
                'groupCollapsed',
            ],
        }, confData || {});
        _this.config.set(dat);
        _this.originalPrefix = _this.name ? [_this.name] : [];
        _this.outputPrefix = _this.name ? [_this.name] : [];
        try {
            if (!isNativeFn(Proxy)) {
                throw new Error('non native');
            }
            _this.proxyHandler = new Proxy(_this, proxyWrapper(_this));
        }
        catch (err) {
            _this.proxyHandler = ProxyPolyfill(_this);
        }
        return _this.proxyHandler;
    }
    StyledLogger.DEBUG_LEVEL = 1; // all methods
    StyledLogger.WARN_LEVEL = 2; //'error', 'assert', 'warn'
    StyledLogger.ERROR_LEVEL = 3; // 'error', 'assert'
    StyledLogger.TRACE_LEVEL = 4; // 'error', 'assert', 'warn', 'trace'
    return StyledLogger;
}(BaseLoader));
var ChildLogger = /** @class */ (function (_super) {
    tslib_es6.__extends(ChildLogger, _super);
    function ChildLogger(name, path, baseLogger, debugMode) {
        var _this = _super.call(this) || this;
        _this.name = name;
        _this.debugMode = false;
        _this.originalPrefix = tslib_es6.__spreadArray(tslib_es6.__spreadArray([], path), [name]);
        _this.outputPrefix = tslib_es6.__spreadArray(tslib_es6.__spreadArray([], path), [name]);
        _this.baseLogger = baseLogger;
        _this.debugMode = debugMode;
        try {
            if (!isNativeFn(Proxy)) {
                throw new Error('non native');
            }
            _this.proxyHandler = new Proxy(_this, proxyWrapper(_this, _this.baseLogger));
        }
        catch (err) {
            _this.proxyHandler = ProxyPolyfill(_this, _this.baseLogger);
        }
        return _this.proxyHandler;
    }
    return ChildLogger;
}(BaseLoader));

exports.ChildLogger = ChildLogger;
exports.default = StyledLogger;
